import React, { useState, useContext, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import MembershipFlowContext from '../../context/membership_flow_context';
import { createQueryString } from '../../../utilities/utilities';
import { CM_PACKAGE_SHORT_NAME } from '../../constants/membership_flow.constants';
import Spinner from '../../../components/react/spinner/spinner';
import AutocompleteBootstrap5 from '../../../components/react/autocompletes/autocomplete_bootstrap_5';
import {
  autocompletePlacesPath,
  autocompleteCompaniesPath,
} from '../../../utilities/api_relative_paths';
import pickBy from 'lodash/pickBy';
import AlertPopup from '../../../components/react/alert_popup/alert_popup';
import { AlertDataType } from '../../../components/react/alert_popup/types';
import { GENERIC_ERROR_MESSAGE } from '../../../components/react/alert_popup/constants';
import { getUserData, updateUserData } from '../../../services/current_user/current_user.service';
import styles from './your_details_for_default_reg.module.scss';

interface AutocompleteEntry {
  id: number | string;
  text: string;
  customOption?: boolean;
}

interface Props {
  setWaitingForEarlyRedirectionCheck: (newBooleanValue: boolean) => void;
}

const YourDetailsForDefaultReg: React.FC<Props> = ({setWaitingForEarlyRedirectionCheck}) => {
  const companyInputValue = useRef<string>('');
  const [alertData, setAlertData] = useState<AlertDataType | null>(null);
  const [goToNextStep, setGoToNextStep] = useState<boolean>(false);
  const { paymentPage, returnTo, packageShortName, country } = useContext(MembershipFlowContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [jobTitleModel, setJobTitleModel] = useState<string>('');

  const [locationModel, setLocationModel] = useState<{ id: string; text: string }[]>(
    country ? [country] : []
  );

  const [companyModel, setCompanyModel] = useState<AutocompleteEntry[]>([]);
  const [showRequiredJobTitle, setShowRequiredJobTitle] = useState(false);
  const [showRequiredLocationError, setShowRequiredLocationError] = useState(false);

  const onFormSubmit = async (event) => {
    event.preventDefault();
    setShowRequiredJobTitle(!jobTitleModel.trim().length);
    setShowRequiredLocationError(!locationModel.length);

    // If any of the required fields are empty, return early
    if (!jobTitleModel.trim().length || !locationModel.length) return;

    setIsLoading(true);
    try {
      // Perform GET request to fetch user ID
      const userData = await getUserData();

      const body = pickBy({
        headline: jobTitleModel.trim(),
        place_key: locationModel[0].id,
        user_declared_company_name: companyModel[0]?.customOption && companyModel[0].text,
        user_declared_company_id: !companyModel[0]?.customOption && companyModel[0]?.id,
      });

      // Once we have retrieved the user ID, patch the user data
      await updateUserData(body, userData.id);
      setIsLoading(false);
      setGoToNextStep(true);
    } catch (error) {
      setIsLoading(false);

      setAlertData({
        type: 'error',
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  };

  const renderRequiredFieldError = (
    <div className='mt-2 text-danger'>Please fill in this field to continue.</div>
  );

  const renderMandatoryJobTitleField = (
    <div className='form-group mb-3'>
      <label className='form-label fw-normal fs-6 text-gray-li' htmlFor='job-title'>
        Job title<span className='text-orange-devex'> *</span>
      </label>
      <input
        id='job-title'
        name='job-title'
        aria-label='Job title input'
        className='form-control rounded-1'
        type='text'
        value={jobTitleModel}
        onChange={({ target }) => setJobTitleModel(target.value)}
      ></input>
      {showRequiredJobTitle && renderRequiredFieldError}
    </div>
  );

  const renderMandatoryLocationField = (
    <div className='form-group mb-3'>
      <label className='form-label fw-normal fs-6 text-gray-li' htmlFor='current-location-input'>
        Current Location<span className='text-orange-devex'> *</span>
      </label>
      <AutocompleteBootstrap5
        id='current-location-input'
        inputProps={{
          id: 'current-location-input',
          className: 'form-control rounded-1',
          'aria-label': 'Current location autocomplete',
        }}
        onSelect={(selected) => setLocationModel(selected ? [selected] : [])}
        placeholder='Start typing your location...'
        minLength={1}
        apiPath={autocompletePlacesPath}
        selected={locationModel}
        filter={{ types: ['Country', 'City'] }}
        clearButton={false}
      />
      {showRequiredLocationError && renderRequiredFieldError}
    </div>
  );

  const renderOptionalOrganizationField = (
    <div className='form-group mb-45'>
      <label className='form-label fw-normal fs-6 text-gray-li' htmlFor='organization-autocomplete'>
        Organization
      </label>
      <AutocompleteBootstrap5
        id='organization-autocomplete'
        inputProps={{
          id: 'organization-autocomplete',
          'aria-label': 'Organization autocomplete',
          className: 'form-control rounded-1',
        }}
        minLength={2}
        newSelectionPrefix=''
        onSelect={(selected) => setCompanyModel(selected ? [selected] : [])}
        placeholder='e.g. Devex'
        // Only allow for a new company entry when there are no more
        // suggestions. If, for example, user type "google" and one of the
        // suggestions is "Google", then they should not be able to add a new
        // company. They should rather select the suggestion.
        allowNew={(suggestions) => !suggestions.length}
        highlightOnlyResult={false}
        apiPath={autocompleteCompaniesPath}
        selected={companyModel}
        filter={{ s: ['processed', 'viewonly'] }}
        clearButton={false}
        onInputChange={(text) => (companyInputValue.current = text)}
        onBlur={() => {
          if (!companyModel.length && companyInputValue.current.trim().length > 0) {
            setCompanyModel([
              {
                id: 'new-id-1',
                text: companyInputValue.current.trim(),
                customOption: true,
              },
            ]);
          }
        }}
      />
    </div>
  );

  const renderContinueButton = (
    <button
      aria-label='Continue button'
      type='submit'
      className='btn btn-dark w-100 btn-lg mt-3 mb-3'
    >
      <strong>Continue</strong>
    </button>
  );

  if (goToNextStep) {
    // In DefaultRegistration context a.k.a when isOrganizationRegistrationFlow prop is false
    // If initial intention is CM, pitch the CA step. Send users to
    // `/register/cm/upgrade-to-ca`
    if (packageShortName === CM_PACKAGE_SHORT_NAME) {
      // Force reset the state of the whole React application. We need to do that in order for
      // users to see a loading animation when UpgradeToCaPitch component loads. That way, if
      // the user is early redirected, the transition is seamless
      setWaitingForEarlyRedirectionCheck(true);

      const nextStepPathForDefaultReg = window.location.pathname.replace(
        '/your-details',
        '/upgrade-to-ca'
      );
      return <Redirect push to={nextStepPathForDefaultReg} />;
    }

    // Users who want to purchase a CA, PRO or PFO are sent to the UpgradeFlow.
    // eslint-disable-next-line max-len
    // `/upgrade/payment?package_short_name=${packageShortName}&payment_page_permalink=${paymentPage}&return_to=${returnTo}`
    const baseUrl = '/upgrade/payment';

    window.location.href =
      baseUrl +
      createQueryString({
        package_short_name: packageShortName,
        payment_page_permalink: paymentPage,
        return_to: returnTo,
      });
  }

  return (
    <>
      <AlertPopup alertData={alertData} setAlertData={setAlertData} />
      <div data-testid='your-details' className={styles.mainContainer}>
        <h2 className={`devex-font-family-serif ${styles.title}`}>Your Details</h2>
        <form
          className='text-start h-auto'
          role='form'
          aria-label='form'
          noValidate
          onSubmit={onFormSubmit}
        >
          {renderMandatoryJobTitleField}
          {renderMandatoryLocationField}
          {renderOptionalOrganizationField}
          {renderContinueButton}
        </form>
        <Spinner isLoading={isLoading} isAbsolute={true}></Spinner>
      </div>
    </>
  );
};

export default YourDetailsForDefaultReg;
